/**
 * CompanyResultsPage - 全社分析結果一覧ページ
 * COMPANY_WIDE権限を持つユーザー向け
 * members-report-2.html のデザインを完全に流用
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import './CompanyResultsPageDesign.scss';
import { analysisApi } from '../api/analysisApi';
import { organizationApi } from '../../admin/api/organizationApi';
import { downloadAllReportsPdf } from '../utils/bulkReportPdf';
import type { AnalysisResult } from '../types';

type DisplayMode = 'list' | 'comparison';

interface UserWithResult {
  userId: string;
  displayName: string;
  position?: string;
  department?: string;
  companyName?: string;
  latestResult?: AnalysisResult;
}

const MAX_COMPARISON_USERS = 6;

// イニシャルを生成するヘルパー関数
const getInitials = (name: string): string => {
  const parts = name.split(/[\s　]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// レーダーチャートのパスを計算（A=135°, B=225°, C=315°, D=45°）
const calculateRadarPath = (scores: { A?: number; B?: number; C?: number; D?: number }): string => {
  const maxVal = 12;
  const center = 126;
  const maxRadius = 105;

  const getPoint = (angle: number, value: number) => {
    const rad = (angle * Math.PI) / 180;
    const r = (value / maxVal) * maxRadius;
    return {
      x: center + r * Math.cos(rad),
      y: center - r * Math.sin(rad),
    };
  };

  const pA = getPoint(135, scores.A || 0);
  const pB = getPoint(225, scores.B || 0);
  const pC = getPoint(315, scores.C || 0);
  const pD = getPoint(45, scores.D || 0);

  return `M${pA.x},${pA.y} L${pB.x},${pB.y} L${pC.x},${pC.y} L${pD.x},${pD.y} Z`;
};

export const CompanyResultsPage: React.FC = () => {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('list');
  const [users, setUsers] = useState<UserWithResult[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [compareResults, setCompareResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  // 全員のPDF一括ダウンロード
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ done: number; total: number } | null>(
    null
  );

  // 絞り込みフィルター
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterName, setFilterName] = useState('');

  // html要素にレポート専用クラスを追加（rem単位の計算を正しくするため）
  // サイドバーメニューのフォントサイズはindex.cssで個別に固定
  useEffect(() => {
    document.documentElement.classList.add('design-report-active');
    return () => {
      document.documentElement.classList.remove('design-report-active');
    };
  }, []);

  // ユーザー一覧を取得
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await analysisApi.getCompanyResults();

      // 会社一覧を取得して、ユーザーの会社名・部門名を取得
      const companies = await organizationApi.getCompanies();

      // 各会社の部門とその社員を取得
      const userDepartmentMap = new Map<string, { companyName: string; departmentName: string }>();

      for (const company of companies) {
        try {
          const departments = await organizationApi.getDepartmentsByCompany(company.id);
          for (const dept of departments) {
            try {
              const employees = await organizationApi.getEmployeesByDepartment(dept.id);
              for (const emp of employees) {
                userDepartmentMap.set(emp.id, {
                  companyName: company.name,
                  departmentName: dept.name,
                });
              }
            } catch {
              // 部門の社員取得に失敗しても続行
            }
          }
        } catch {
          // 部門取得に失敗しても続行
        }
      }

      const userMap = new Map<string, UserWithResult>();

      for (const result of response.results) {
        if (!userMap.has(result.userId)) {
          const userOrgInfo = userDepartmentMap.get(result.userId);
          userMap.set(result.userId, {
            userId: result.userId,
            displayName: result.userName || result.userId,
            position: '',
            department: userOrgInfo?.departmentName || '',
            companyName: userOrgInfo?.companyName || '',
            latestResult: result,
          });
        }
      }

      setUsers(Array.from(userMap.values()));
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('ユーザー一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // モード変更
  const handleModeChange = useCallback((mode: DisplayMode) => {
    setDisplayMode(mode);
    setSelectedUserIds([]);
    setCompareResults([]);
    setWarningMessage(null);
  }, []);

  // 一覧モード: ユーザークリックで個人レポートを新しいウィンドウで開く
  const handleOpenIndividualReport = useCallback((userId: string) => {
    // 配布物と同じ A4横2ページのレイアウトを HTML で表示する画面を開く。
    // 右上の「PDFとして保存」で、表示中と同一内容の単票PDFを保存できる。
    // （モバイルWebView用の /embed/report/employee/:userId は縦1カラムのまま別経路）
    window.open(`/analysis/personal-report/${userId}`, '_blank', 'noopener');
  }, []);

  // 比較モード: ユーザー選択
  const handleCompareSelect = useCallback(
    (userId: string) => {
      setWarningMessage(null);

      setSelectedUserIds((prev) => {
        if (prev.includes(userId)) {
          const newSelection = prev.filter((id) => id !== userId);
          setCompareResults((prevResults) => prevResults.filter((r) => r.userId !== userId));
          return newSelection;
        } else {
          if (prev.length >= MAX_COMPARISON_USERS) {
            setWarningMessage('最大6人までしか選択できません');
            return prev;
          }
          const newSelection = [...prev, userId];

          const user = users.find((u) => u.userId === userId);
          if (user?.latestResult) {
            setCompareResults((prevResults) => [...prevResults, user.latestResult!]);
          }

          return newSelection;
        }
      });
    },
    [users]
  );

  // 絞り込みフィルタ
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const nameMatch =
        filterName === '' ||
        user.displayName.toLowerCase().includes(filterName.toLowerCase()) ||
        user.userId.toLowerCase().includes(filterName.toLowerCase());
      const deptMatch =
        filterDepartment === '' ||
        (user.department && user.department.toLowerCase().includes(filterDepartment.toLowerCase()));
      const posMatch =
        filterPosition === '' ||
        (user.position && user.position.toLowerCase().includes(filterPosition.toLowerCase()));
      return nameMatch && deptMatch && posMatch;
    });
  }, [users, filterName, filterDepartment, filterPosition]);

  // 条件クリア
  const handleClearFilters = useCallback(() => {
    setFilterDepartment('');
    setFilterPosition('');
    setFilterName('');
  }, []);

  // 全員のPDFを一括ダウンロード (現在の絞り込み結果が対象)
  const handleDownloadAll = useCallback(async () => {
    if (downloading || filteredUsers.length === 0) return;
    setWarningMessage(null);
    setDownloading(true);
    setDownloadProgress({ done: 0, total: filteredUsers.length });
    try {
      const targets = filteredUsers.map((u) => ({
        userId: u.userId,
        displayName: u.displayName,
      }));
      const { skipped } = await downloadAllReportsPdf(targets, (done, total) => {
        setDownloadProgress({ done, total });
      });
      if (skipped.length > 0) {
        setWarningMessage(
          `${skipped.length}件は分析結果が無い等の理由でPDFに含められませんでした。`
        );
      }
    } catch (err) {
      console.error('全員PDFの生成に失敗しました:', err);
      setWarningMessage(err instanceof Error ? err.message : '全員PDFの生成に失敗しました。');
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  }, [downloading, filteredUsers]);

  // 比較データ
  const comparisonData = useMemo(() => {
    return compareResults.map((result) => ({
      ...result,
      userName: users.find((u) => u.userId === result.userId)?.displayName || result.userId,
    }));
  }, [compareResults, users]);

  if (loading) {
    return (
      <div className="company-results-design">
        <div className="loading-container">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-results-design">
        <div className="error-container">{error}</div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="company-results-design">
        <div className="empty-container">対象者がいません</div>
      </div>
    );
  }

  return (
    <div className="company-results-design">
      <div className="c-column-2">
        {/* 左サイドバー */}
        <aside className="c-column-2_aside">
          <div className="c-column-2_aside_inner">
            {/* フィルターフォーム */}
            <form className="filter" onSubmit={(e) => e.preventDefault()}>
              <h2 className="filter_ttl">絞り込み</h2>
              <label className="filter_label">
                <input
                  className="filter_label_input"
                  type="text"
                  placeholder="所属"
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                />
              </label>
              <label className="filter_label">
                <input
                  className="filter_label_input"
                  type="text"
                  placeholder="役職"
                  value={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.value)}
                />
              </label>
              <label className="filter_label">
                <input
                  className="filter_label_input"
                  type="text"
                  placeholder="社員名"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </label>
              <button className="filter_reset" type="button" onClick={handleClearFilters}>
                条件をクリア
              </button>
            </form>

            {/* 表示セクション */}
            <div className="display">
              <p className="display_count">
                表示件数 <span className="display_count_val">{filteredUsers.length}</span>件
              </p>

              {/* 全員のPDFを一括ダウンロード (現在の絞り込み結果が対象) */}
              <button
                type="button"
                className="bulk-download-btn"
                onClick={handleDownloadAll}
                disabled={downloading || filteredUsers.length === 0}
              >
                {downloading
                  ? downloadProgress
                    ? `PDF作成中… (${downloadProgress.done}/${downloadProgress.total})`
                    : 'PDF作成中…'
                  : `全員のPDFをダウンロード（${filteredUsers.length}名）`}
              </button>
              {downloading && (
                <p className="bulk-download-note">
                  作成には少し時間がかかります。完了までこのページを開いたままにしてください。
                </p>
              )}

              <div className="display_toggle">
                <label className="display_toggle_label">
                  <input
                    className="display_toggle_label_input"
                    type="radio"
                    name="display-radio"
                    checked={displayMode === 'list'}
                    onChange={() => handleModeChange('list')}
                  />
                  <span className="display_toggle_label_txt">個別に表示する</span>
                </label>
                {/* 現在のフェーズでは社員個人にレポートを表示しない */}
                {/* <label className="display_toggle_label">
                  <input
                    className="display_toggle_label_input"
                    type="radio"
                    name="display-radio"
                    checked={displayMode === 'comparison'}
                    onChange={() => handleModeChange('comparison')}
                  />
                  <span className="display_toggle_label_txt">比較表示する</span>
                </label> */}
              </div>

              {warningMessage && <div className="warning-message">{warningMessage}</div>}

              <div className="display_box">
                {/* 個別表示モード */}
                <div
                  className={`display_box_item _single ${displayMode === 'list' ? '_active' : ''}`}
                >
                  <ul className="display_box_list">
                    {filteredUsers.map((user) => (
                      <li key={user.userId} className="display_box_list_item">
                        <div
                          className="display_box_list_label"
                          onClick={() => handleOpenIndividualReport(user.userId)}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="display_box_list_label_inner">
                            <span className="display_box_list_label_icon">
                              {getInitials(user.displayName)}
                            </span>
                            <span className="display_box_list_label_pos">
                              {user.position || user.department || ''}
                            </span>
                            <span className="display_box_list_label_name">{user.displayName}</span>
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 比較表示モード */}
                <div
                  className={`display_box_item _compare ${displayMode === 'comparison' ? '_active' : ''}`}
                >
                  <ul className="display_box_list">
                    {filteredUsers.map((user) => {
                      const isSelected = selectedUserIds.includes(user.userId);
                      return (
                        <li key={user.userId} className="display_box_list_item">
                          <label
                            className={`display_box_list_label _checkbox ${isSelected ? '_checked' : ''}`}
                          >
                            <span
                              className={`display_box_list_label_inner ${isSelected ? '_selected' : ''}`}
                            >
                              <input
                                className="display_box_list_label_input"
                                type="checkbox"
                                name="display-compare"
                                checked={isSelected}
                                onChange={() => handleCompareSelect(user.userId)}
                              />
                              <span className="display_box_list_label_icon">
                                {getInitials(user.displayName)}
                              </span>
                              <span className="display_box_list_label_pos">
                                {user.position || user.department || ''}
                              </span>
                              <span className="display_box_list_label_name">
                                {user.displayName}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* 右パネル: 結果表示 - オリジナルデザインと同じクラス構成 */}
        <section className="c-sec-report c-column-2_main">
          <div className="inner">
            {/* 一覧モード: 説明表示 */}
            {displayMode === 'list' && (
              <div className="placeholder-text">
                左のリストからユーザーをクリックすると、新しいウィンドウでレポートが開きます
              </div>
            )}

            {/* 比較モード: 未選択時 */}
            {displayMode === 'comparison' && selectedUserIds.length === 0 && (
              <div className="placeholder-text">
                左のリストからユーザーを選択してください（最大6人）
              </div>
            )}

            {/* 比較モード: 選択時 - members-report-2.html のデザインを完全に流用 */}
            {displayMode === 'comparison' && selectedUserIds.length > 0 && (
              <ul className="report-compare">
                {comparisonData.map((result) => {
                  const scores = result.scores.scores;
                  const pathD = calculateRadarPath(scores);

                  return (
                    <li key={result.id} className="report-compare_item">
                      <h2 className="report-compare_label">
                        <span className="report-compare_label_icon">
                          {getInitials(result.userName || '')}
                        </span>
                        {result.userName}
                      </h2>
                      <div className="report-compare_inner">
                        <div className="report-compare_radar js-radar-chart-val">
                          <svg className="js-radar-chart" viewBox="0 0 252 252">
                            <path className="js-radar-chart_line" d={pathD} />
                          </svg>
                          <h3 className="report-compare_radar_ttl">
                            <span className="report-compare_radar_ttl_txt">
                              <span className="report-compare_radar_ttl_txt_lg">A</span>論理
                            </span>
                            <span className="report-compare_radar_ttl_val">
                              <span className="report-compare_radar_ttl_val_lg js-radar-chart-val-a">
                                {scores.A || 0}
                              </span>
                              /10
                            </span>
                          </h3>
                          <h3 className="report-compare_radar_ttl">
                            <span className="report-compare_radar_ttl_txt">
                              <span className="report-compare_radar_ttl_txt_lg">B</span>秩序
                            </span>
                            <span className="report-compare_radar_ttl_val">
                              <span className="report-compare_radar_ttl_val_lg js-radar-chart-val-b">
                                {scores.B || 0}
                              </span>
                              /10
                            </span>
                          </h3>
                          <h3 className="report-compare_radar_ttl">
                            <span className="report-compare_radar_ttl_txt">
                              <span className="report-compare_radar_ttl_txt_lg">C</span>共感
                            </span>
                            <span className="report-compare_radar_ttl_val">
                              <span className="report-compare_radar_ttl_val_lg js-radar-chart-val-c">
                                {scores.C || 0}
                              </span>
                              /10
                            </span>
                          </h3>
                          <h3 className="report-compare_radar_ttl">
                            <span className="report-compare_radar_ttl_txt">
                              <span className="report-compare_radar_ttl_txt_lg">D</span>創造
                            </span>
                            <span className="report-compare_radar_ttl_val">
                              <span className="report-compare_radar_ttl_val_lg js-radar-chart-val-d">
                                {scores.D || 0}
                              </span>
                              /10
                            </span>
                          </h3>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CompanyResultsPage;
